import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SystemAlert from '../../common/SystemAlert';
import axios from 'axios';

// Utility to get logged-in user info from token/cookie/context
import Cookies from '../../common/Cookies';
import { API_URL } from '../../common/Constants';


export default function UserProfileModal({ open, onClose, user }) {
    useEffect(() => {
        console.log('UserProfileModal opened:', { user, open });
    }, [user, open]);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    // Password visibility toggles
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        async function fetchUser() {
            console.log('Fetching user data:', { user, open });
            if (open) {
                try {
                    const res = await axios.get(`${API_URL}/users/fetchUp`, {
                        headers: {
                            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                        }
                    });
                    const u = res.data;
                    console.log('Fetched user from backend:', u);
                    setUserData(u);
                    setForm({
                        firstName: u.first_name || '',
                        lastName: u.last_name || '',
                        email: u.email || '',
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                    });
                } catch (err) {
                    console.log('Failed to fetch user, using passed user:', user);
                    setForm({
                        firstName: user.firstName || '',
                        lastName: user.lastName || '',
                        email: user.email || '',
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                    });
                }
            }
        }
        fetchUser();
    }, [user, open]);

    useEffect(() => {
        console.log('Form state:', form);
    }, [form]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Validate current password before update
    const validatePassword = async () => {
        console.log('Validating current password:', form.currentPassword);
        try {
            const email = userData?.email || user?.email || form.email;
            const res = await axios.post(`${API_URL}/login/validate`, null, {
                params: {
                    email: form.email,
                    password: form.currentPassword,
                }
            });
            return res.data && res.data.status === 'success';
        } catch {
            return false;
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setAlert(null);
        setShowAlert(false);
        // Validate current password if changing password
        if (form.newPassword) {
            // Prevent using current password as new password
            if (form.newPassword === form.currentPassword) {
                setAlert({ severity: 'error', message: 'New password cannot be the same as the current password.' });
                setShowAlert(true);
                setLoading(false);
                return;
            }
            const valid = await validatePassword();
            if (!valid) {
                setAlert({ severity: 'error', message: 'Current password is incorrect.' });
                setShowAlert(true);
                setLoading(false);
                return;
            }
            // Confirm new password matches
            if (form.newPassword !== form.confirmPassword) {
                setAlert({ severity: 'error', message: 'New password and confirm password do not match.' });
                setShowAlert(true);
                setLoading(false);
                return;
            }
        }
        // Update user info
        try {
            const id = userData?.id || user?.id;
            const email = form.email || userData?.email || user?.email;
            const payload = {
                id,
                first_name: form.firstName,
                last_name: form.lastName,
                email,
                ...(form.newPassword ? { password: form.newPassword } : {}),
            };
            await axios.post(`${API_URL}/users/updateUp`, payload, {
                headers: {
                    'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
                }
            });
            // Show specific alert if password was updated
            if (form.newPassword) {
                setAlert({ severity: 'success', message: 'Password updated successfully.' });
            } else {
                setAlert({ severity: 'success', message: 'Profile updated successfully.' });
            }
            setShowAlert(true);
            setLoading(false);
            // Delay closing the modal to allow user to see the success alert
            setTimeout(() => {
                onClose(true); // signal success
            }, 1500);
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to update profile.' });
            setShowAlert(true);
            setLoading(false);
        }
    };

    // Log open prop changes
    React.useEffect(() => {
        console.log('[UserProfileModal] open changed:', open);
    }, [open]);

    // Only allow explicit closes; ignore backdrop/Escape
    const handleDialogClose = (event, reason) => {
        console.log('[UserProfileModal] requested close, reason:', reason);
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // ignore automatic closes
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            disableEscapeKeyDown
            keepMounted
        >
            <DialogTitle>
                Edit Profile
                <IconButton
                    aria-label="close"
                    onClick={() => onClose(false)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    margin="normal"
                    label="Username / Email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    disabled={true}
                    fullWidth
                />
                <TextField
                    margin="normal"
                    label="First Name"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    fullWidth
                />
                <TextField
                    margin="normal"
                    label="Last Name"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    fullWidth
                />
                <TextField
                    margin="normal"
                    label="Current Password"
                    name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={form.currentPassword}
                    onChange={handleChange}
                    fullWidth
                    required={!!form.newPassword}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle current password visibility"
                                    onClick={() => setShowCurrentPassword((show) => !show)}
                                    edge="end"
                                >
                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    margin="normal"
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={form.newPassword}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle new password visibility"
                                    onClick={() => setShowNewPassword((show) => !show)}
                                    edge="end"
                                >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    margin="normal"
                    label="Confirm Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle confirm password visibility"
                                    onClick={() => setShowConfirmPassword((show) => !show)}
                                    edge="end"
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={loading} variant="contained" color="primary">
                    Save
                </Button>
            </DialogActions>
            <SystemAlert
                open={showAlert}
                severity={alert?.severity}
                message={alert?.message}
                onClose={() => setShowAlert(false)}
            />
        </Dialog>
    );
}

UserProfileModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    user: PropTypes.object,
};
